'use client;';

import { createContext, useCallback, useContext, useMemo, useState } from 'react';

import { useAuthCtx } from '@/components/providers/auth-provider';
import { useMeCtx } from '@/components/providers/v1-me-provider';
import { useOfficeCtx } from '@/components/providers/v1-office-provider';

export const FEATURE_FLAGS = [
  '/voice-agent/calls',
  '/voice-agent/calls/outcome',
  '/voice-agent/settings',
  '/voice-agent/settings/customer/create',
  '/voice-agent/voices',
  '/customers',
  '/customers/create',
  '/customers/service_plan/create',
  '/settings',
  '/external-calls',
] as const;
export type FeatureFlagName = (typeof FEATURE_FLAGS)[number];
const SOLEA_DEFAULT_FEATURE_FLAGS: FeatureFlagName[] = [
  '/voice-agent/calls',
  '/voice-agent/calls/outcome',
  '/voice-agent/settings',
  '/voice-agent/settings/customer/create',
  '/customers',
  '/settings',
  '/external-calls',
];

interface ContextType {
  isFeatureEnabled: (feature: FeatureFlagName) => boolean;
  setFeatureFlag: (name: FeatureFlagName, enabled: boolean) => void;
  enableAll: () => void;
  reset: () => void;
}

const Context = createContext<ContextType | null>(null);

export function useFeatureFlagCtx() {
  const ctx = useContext(Context);
  if (!ctx) {
    throw new Error('useCtx must be used within a Provider');
  }
  return ctx;
}

interface Props {
  children: React.ReactNode;
}

type FeatureFlagsStored = {
  flags: {
    name: FeatureFlagName;
    enabled: boolean;
  }[];
};

const FeatureFlagsStore = {
  key: 'feature-flags',
  read: (): FeatureFlagsStored['flags'] => {
    const value = localStorage.getItem(FeatureFlagsStore.key);
    if (!value) {
      return [];
    }
    try {
      const v = JSON.parse(value) as FeatureFlagsStored;
      return v.flags;
    } catch {
      return [];
    }
  },
  store: (v: FeatureFlagsStored) => {
    localStorage.setItem(FeatureFlagsStore.key, JSON.stringify(v));
  },
};

export function FeatureFlagProvider({ children }: Props) {
  const { isSuperuser } = useAuthCtx();
  const defaultFeatureFlags = useDefaultFeatureFlags();
  const [storedFeatureFlags, setStoredFeatureFlags] = useState(() => {
    if (typeof window !== undefined) {
      return FeatureFlagsStore.read();
    }
    return [];
  });

  // A map of feature flags to their enabled state
  // For superusers, local storage overrides take precedence
  // Otherwise, use default features (i.e. office & employee specific)
  const featureFlags = useMemo(() => {
    const flags = new Map<FeatureFlagName, boolean>();
    if (isSuperuser) {
      storedFeatureFlags.forEach((v) => {
        flags.set(v.name, v.enabled);
      });
    }
    defaultFeatureFlags.forEach((flagName) => {
      if (!flags.has(flagName)) {
        flags.set(flagName, true);
      }
    });
    return flags;
  }, [isSuperuser, defaultFeatureFlags, storedFeatureFlags]);

  const isFeatureEnabled = useCallback(
    (feature: FeatureFlagName) => {
      return featureFlags.get(feature) ?? false;
    },
    [featureFlags]
  );

  const setFeatureFlag = useCallback(
    (name: FeatureFlagName, enabled: boolean) => {
      let newFlags = [];
      if (storedFeatureFlags.find((v) => v.name === name)) {
        newFlags = storedFeatureFlags.map((v) => (v.name === name ? { name: v.name, enabled: enabled } : v));
      } else {
        newFlags = [...storedFeatureFlags, { name, enabled }];
      }
      setStoredFeatureFlags(newFlags);
      FeatureFlagsStore.store({ flags: newFlags });
    },
    [storedFeatureFlags]
  );

  const enableAll = useCallback(() => {
    const newFlags = FEATURE_FLAGS.map((name) => ({ name, enabled: true }));
    setStoredFeatureFlags(newFlags);
    FeatureFlagsStore.store({ flags: newFlags });
  }, []);

  const reset = useCallback(() => {
    setStoredFeatureFlags([]);
    FeatureFlagsStore.store({ flags: [] });
  }, []);

  return <Context.Provider value={{ isFeatureEnabled, setFeatureFlag, enableAll, reset }}>{children}</Context.Provider>;
}

export function useFeatureFlag(feature: FeatureFlagName) {
  const { isFeatureEnabled } = useFeatureFlagCtx();
  return isFeatureEnabled(feature);
}

const OFFICE_NAME_TO_FEATURE_FLAGS: {
  office: string;
  flags: FeatureFlagName[];
}[] = [
  {
    office: 'solea',
    flags: [...SOLEA_DEFAULT_FEATURE_FLAGS],
  },
  {
    office: 'horizon',
    flags: ['/customers'],
  },
  {
    office: 'moxie',
    flags: ['/external-calls'],
  },
  {
    office: 'atascocita',
    flags: ['/voice-agent/calls', '/voice-agent/settings', '/settings'],
  },
  {
    office: 'native',
    flags: ['/voice-agent/calls', '/voice-agent/voices'],
  },
  {
    office: 'greencore',
    flags: ['/voice-agent/calls'],
  },
  {
    office: 'hydrex',
    flags: ['/voice-agent/calls', '/voice-agent/settings', '/voice-agent/settings/customer/create', '/settings'],
  },
  {
    office: 'aloha',
    flags: ['/voice-agent/calls', '/voice-agent/settings', '/settings', '/customers'],
  },
];

const EMPLOYEE_EMAIL_TO_FEATURE_FLAGS: {
  email: string;
  flags: FeatureFlagName[];
}[] = [
  {
    email: 'ecampos@horizonpestcontrol.com',
    flags: ['/voice-agent/calls', '/customers', '/settings'],
  },
  {
    email: 'jmulcachy@horizonpestcontrol.com',
    flags: ['/customers', '/settings'],
  },
];

function useDefaultFeatureFlags() {
  const { isSuperuser } = useAuthCtx();
  const { office } = useOfficeCtx();
  const { me } = useMeCtx();

  return useMemo(() => {
    if (isSuperuser) {
      return [...SOLEA_DEFAULT_FEATURE_FLAGS];
    }
    const employeeFlags = getEmployeeFeatureFlags(me?.email ?? '');
    if (employeeFlags.length > 0) {
      return employeeFlags;
    }
    return getOfficeFeatureFlags(office.name);
  }, [isSuperuser, office, me?.email]);
}

function getEmployeeFeatureFlags(employeeEmail: string): FeatureFlagName[] {
  const lower = employeeEmail.toLowerCase();
  return EMPLOYEE_EMAIL_TO_FEATURE_FLAGS.find((v) => lower.includes(v.email))?.flags ?? [];
}

function getOfficeFeatureFlags(office: string): FeatureFlagName[] {
  const lower = office.toLowerCase();
  return OFFICE_NAME_TO_FEATURE_FLAGS.find((v) => lower.includes(v.office))?.flags ?? [];
}

interface FeatureFlagGuardProps {
  feature: FeatureFlagName;
  children: React.ReactNode;
}

export function FeatureFlagGuard({ feature, children }: FeatureFlagGuardProps) {
  const { isFeatureEnabled } = useFeatureFlagCtx();
  if (!isFeatureEnabled(feature)) {
    return null;
  }
  return children;
}
